/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React from "react";
import PropTypes from "prop-types";
import { simpleFetch } from "../../restWrapper";
import errorMsg from "../../assets/json/errMsg.json";
import "./CollaborationSettings.css";

// CP-300 / CP-299 — two switches decide whether this organization appears on
// the Plug marketplace, and only one of them is ours.
//
// plugListingEligible belongs to CheckPlus staff and defaults to false:
// nonprofits are added to the marketplace one at a time. plugListingOptIn is
// this organization's own and defaults to *on* — it is a way out, not a way
// in. So the page is a withdrawal control, not an opt-in form.
//
// Absent counts as enabled. Organizations predating the field have no value
// stored and have not withdrawn, so the test is `!== false` rather than a
// check for an explicit true.
const OPT_IN_FIELD = "plugListingOptIn";
const ELIGIBLE_FIELD = "plugListingEligible";

export function orgHasWithdrawn(organizationObj) {
  return !!organizationObj && organizationObj[OPT_IN_FIELD] === false;
}

// Mirrors the Admin panel's derived state (CP-299), from the organization's
// point of view rather than staff's.
//
// Withdrawal is checked before eligibility on purpose. An organization that
// withdrew and was later de-listed by staff would otherwise be shown the
// not-listed copy — "nothing is needed from you" — which is false for it: its
// own opt-out is still stored, so it would stay off Plug even once staff
// re-list it, and it would have no way to undo that in the meantime. The
// organization's own choice is the one it can act on, so it comes first.
//
// CP-304: an explicit paymentReady:false (no payout destination yet) is its
// own state — the org can edit profile and impact, but is not listed or
// payable on Plug until bank/KYC setup is finished.
export function plugListingStateOf(organizationObj) {
  if (!organizationObj) return "blocked";
  if (organizationObj.organizationPermitted !== "approved") return "blocked";
  if (orgHasWithdrawn(organizationObj)) return "withdrawn";
  if (organizationObj.paymentReady === false) return "payoutPending";
  if (!organizationObj[ELIGIBLE_FIELD]) return "notListed";
  return "listed";
}

class CollaborationSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      listingLoading: false,
      // Mirrors the persisted value so the switch can move immediately and be
      // put back if the write fails.
      optedIn: !orgHasWithdrawn(props.organizationObj),
      confirmingWithdraw: false,
      listingError: null,
    };
    this.askToWithdraw = this.askToWithdraw.bind(this);
    this.cancelWithdraw = this.cancelWithdraw.bind(this);
    this.confirmWithdraw = this.confirmWithdraw.bind(this);
    this.rejoin = this.rejoin.bind(this);
  }

  // The constructor snapshot is only correct if the organization was already
  // loaded when this mounted. OrgSettings gates on that, but the component is
  // exported and should not depend on its parent getting the order right:
  // resync whenever the stored value changes underneath us, unless a write of
  // our own is still in flight.
  componentDidUpdate(prevProps) {
    if (this.state.listingLoading) return;
    const was = prevProps.organizationObj &&
      prevProps.organizationObj[OPT_IN_FIELD];
    const now = this.props.organizationObj &&
      this.props.organizationObj[OPT_IN_FIELD];
    if (was === now) return;
    const optedIn = !orgHasWithdrawn(this.props.organizationObj);
    if (optedIn !== this.state.optedIn) this.setState({ optedIn: optedIn });
  }

  // Must never throw. This runs inside the write's .then, and the .catch
  // calls it again — so a pack missing this block would throw twice, the
  // second time unhandled, leaving listingLoading set and the switch stuck in
  // its optimistic position showing a withdrawal that did not happen.
  errText(key) {
    const packs = [errorMsg[this.props.language], errorMsg.en];
    for (let i = 0; i < packs.length; i++) {
      const block = packs[i] && packs[i].CollaborationSettings;
      if (block && block[key]) return block[key];
    }
    // No message to show, but the caller still reverts — a silent revert
    // beats a stuck switch.
    return "";
  }

  // Withdrawing removes the organization from a public surface, so it is
  // confirmed rather than applied on a single click. Rejoining is not — it
  // only restores the default.
  askToWithdraw() {
    this.setState({ confirmingWithdraw: true, listingError: null });
  }

  cancelWithdraw() {
    this.setState({ confirmingWithdraw: false });
  }

  confirmWithdraw() {
    this.setState({ confirmingWithdraw: false });
    this.writeOptIn(false);
  }

  rejoin() {
    this.writeOptIn(true);
  }

  writeOptIn(next) {
    if (this.state.listingLoading) return;
    const previous = this.state.optedIn;
    this.setState({ optedIn: next, listingLoading: true, listingError: null });

    simpleFetch("/organizations", "PUT", { [OPT_IN_FIELD]: next })
      .then((res) => {
        if (res.status === 204 || res.status === 200) {
          this.setState({ listingLoading: false });
          if (this.props.onListingChange) this.props.onListingChange(next);
          return;
        }
        // Put the switch back rather than leaving the organization believing
        // it has left a marketplace it is still on.
        this.setState({
          optedIn: previous,
          listingLoading: false,
          listingError: this.errText("listingFail"),
        });
      })
      .catch(() => {
        this.setState({
          optedIn: previous,
          listingLoading: false,
          listingError: this.errText("listingFail"),
        });
      });
  }

  renderListing() {
    const m = this.props.languageObj;
    const { organizationObj } = this.props;
    // Derive from the live switch value rather than the prop, so the panel
    // updates with the toggle instead of waiting for a refetch.
    const state = plugListingStateOf({
      ...organizationObj,
      [OPT_IN_FIELD]: this.state.optedIn,
    });
    const canAct = state === "listed" || state === "withdrawn";

    return (
      <section className="CollaborationSettings-card">
        <div className={"CollaborationSettings-status is-" + state}>
          <span className="CollaborationSettings-statusDot" />
          <div>
            <div className="CollaborationSettings-statusLabel">
              {m.stateLabel[state]}
            </div>
            <p className="CollaborationSettings-statusHint">
              {m.stateHint[state]}
            </p>
          </div>
        </div>

        {/* What being listed actually means, stated on the page rather than
            assumed. A nonprofit that never opens this page is on Plug, so it
            has to be able to find out here what it is part of. */}
        <ul className="CollaborationSettings-facts">
          {m.facts.map((fact, i) => (
            <li key={"fact" + i}>{fact}</li>
          ))}
        </ul>

        {canAct && !this.state.confirmingWithdraw && (
          <div className="CollaborationSettings-actions">
            {this.state.optedIn ? (
              <button
                type="button"
                className="CollaborationSettings-btn CollaborationSettings-btnDanger"
                onClick={this.askToWithdraw}
                disabled={this.state.listingLoading}
              >
                {m.withdrawBtn}
              </button>
            ) : (
              <button
                type="button"
                className="CollaborationSettings-btn CollaborationSettings-btnPrimary"
                onClick={this.rejoin}
                disabled={this.state.listingLoading}
              >
                {m.rejoinBtn}
              </button>
            )}
          </div>
        )}

        {this.state.confirmingWithdraw && (
          <div className="CollaborationSettings-confirm">
            <p className="CollaborationSettings-confirmText">
              {m.withdrawConfirm}
            </p>
            <div className="CollaborationSettings-actions">
              <button
                type="button"
                className="CollaborationSettings-btn"
                onClick={this.cancelWithdraw}
              >
                {m.cancelBtn}
              </button>
              <button
                type="button"
                className="CollaborationSettings-btn CollaborationSettings-btnDanger"
                onClick={this.confirmWithdraw}
              >
                {m.withdrawConfirmBtn}
              </button>
            </div>
          </div>
        )}

        {this.state.listingError && (
          <div className="CollaborationSettings-error">
            {this.state.listingError}
          </div>
        )}
      </section>
    );
  }

  render() {
    const m = this.props.languageObj;
    // OrgSettings passes `languageObj.Collaboration || {}`, which reads as
    // defensive but is not: renderListing reaches straight into m.stateLabel
    // and m.facts, so an empty object would throw and take the whole settings
    // page down with it. A locale shipped without this block should cost this
    // panel, not everything around it.
    // Every key renderListing dereferences, not just some of them: a partial
    // block — a translator adding stateLabel and facts but missing stateHint
    // — is exactly the case this guard exists for.
    if (!m || !m.stateLabel || !m.stateHint || !m.facts) return null;

    const { organizationObj } = this.props;
    // A missing organizationType means nonprofit, matching the schema default
    // — an organization must never lose access to its own withdrawal switch
    // because a field was never set.
    const isNonprofit =
      !!organizationObj &&
      (organizationObj.organizationType === "nonprofit" ||
        !organizationObj.organizationType);

    if (!isNonprofit) {
      return (
        <div className="CollaborationSettings-view">
          <h2 className="CollaborationSettings-title">{m.title}</h2>
          <div className="CollaborationSettings-empty">{m.nonprofitOnly}</div>
        </div>
      );
    }

    return (
      <div className="CollaborationSettings-view">
        <h2 className="CollaborationSettings-title">{m.title}</h2>
        <p className="CollaborationSettings-intro">{m.intro}</p>
        {this.renderListing()}
      </div>
    );
  }
}

CollaborationSettings.propTypes = {
  organizationObj: PropTypes.object,
  language: PropTypes.string,
  languageObj: PropTypes.object,
  onListingChange: PropTypes.func,
};

export default CollaborationSettings;
